// @flow

import React from "react";
import classNames from "classnames";

import optionResolver from "../utils/options";
import type { SchemaTableWidgetProps } from "../types";

import styles from "./number.scss";

export type NumberWidgetState = $Exact<{
  touched: boolean,
}>;

export class NumberWidget extends React.Component<
  SchemaTableWidgetProps<number>,
  NumberWidgetState,
> {
  state = {
    touched: false,
  };

  componentDidUpdate(prevProps: SchemaTableWidgetProps<number>) {
    // this will only happen as part of a request to hide errors
    if (!this.props.touched && prevProps.touched) {
      this.setState({ touched: false });
    }
  }

  handleChange = (event: SyntheticEvent<HTMLInputElement>) => {
    const value: number = event.currentTarget.value ? +event.currentTarget.value : 0;
    const next: number = this.sanitize(value);
    this.props.onChange(next);
  };

  handleTouched = (event: SyntheticEvent<HTMLInputElement>) => {
    this.setState({ touched: true });
  };

  sanitize = (next: number): number => {
    const minimum = optionResolver.resolveNumber(
      (this.props.options || {}).minimum,
      this.props.row,
    );
    const maximum = optionResolver.resolveNumber(
      (this.props.options || {}).maximum,
      this.props.row,
    );

    if (typeof minimum === "number" && next < minimum) {
      return minimum;
    }

    if (typeof maximum === "number" && next > maximum) {
      return maximum;
    }

    return next;
  };

  hasUiSchemaError = (value: number): boolean => {
    if (!this.state.touched) {
      return false;
    }

    if (this.props.required && (value === undefined || value === null || isNaN(value))) {
      return true;
    }

    const options = this.props.options || {};

    const minimum = optionResolver.resolveNumber(options.minimum, this.props.row);
    if (minimum && value && value < minimum) {
      return true;
    }

    const maximum = optionResolver.resolveNumber(options.maximum, this.props.row);
    if (maximum && value && value > maximum) {
      return true;
    }

    return false;
  };

  hasError = (value: number): boolean => {
    return !!this.props.error || this.hasUiSchemaError(value);
  };

  render() {
    const options = this.props.options;
    const disabled = optionResolver.resolveBoolean((options || {}).disabled, this.props.row);
    const minimum = optionResolver.resolveNumber((options || {}).minimum, this.props.row);
    const maximum = optionResolver.resolveNumber((options || {}).maximum, this.props.row);

    if (!this.props.active) {
      return (
        <div
          className={classNames("number__readonly", {
            number__disabled: disabled,
          })}
        >
          {this.props.data}
        </div>
      );
    }

    return (
      <input
        type="number"
        className={classNames("form-control", {
          number__disabled: disabled,
          number__error: this.hasError(this.props.data),
        })}
        value={this.props.data || ""}
        required={this.props.required}
        min={minimum}
        max={maximum}
        onChange={this.handleChange}
        onBlur={this.handleTouched}
        autoFocus="autofocus"
      />
    );
  }
}

export default NumberWidget;
